/**
 * GeoJSON Preview & Study Page
 *
 * Allows users to explore GeoJSON datasets before creating quizzes.
 * Shows dataset metadata, properties, sample features, and statistics.
 *
 * Flow: Browse → Preview (here) → Studio
 */

import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, MapPin, FileJson, Info, Database, Map as MapIconLucide } from "lucide-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookMascot } from "@/components/ui/BookMascot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ExplorationMap } from "@/components/shared/ExplorationMap";
import { QuizCard } from "@/components/quiz/QuizCard";
import { SizeBadge } from "@/components/shared/SizeBadge";
import { geojsonDatasets } from "@/data/geojsonDatasets";
import { fetchGeoJSONFromUrl } from "@/utils/geojsonLoader";
import { db } from "@/db/database";
import type { QuizConfig } from "@/db/types";
import type { FeatureCollection } from "geojson";

export function GeoJSONPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geojson, setGeojson] = useState<FeatureCollection | null>(null);
  const [selectedLabelProperty, setSelectedLabelProperty] = useState<string | null>(null);
  const [quizzes, setQuizzes] = useState<QuizConfig[]>([]);

  // Find dataset metadata
  const dataset = geojsonDatasets.find((ds) => ds.id === id);

  // Auto-select first property key as default label when data loads
  useEffect(() => {
    if (dataset && !selectedLabelProperty) {
      setSelectedLabelProperty(dataset.propertyKeys[0] || null);
    }
  }, [dataset, selectedLabelProperty]);

  // Load GeoJSON data
  useEffect(() => {
    if (!dataset) {
      setError("Dataset not found");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchGeoJSONFromUrl(dataset.path)
      .then((data) => {
        setGeojson(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load GeoJSON:", err);
        setError(err instanceof Error ? err.message : "Failed to load dataset");
        setLoading(false);
      });
  }, [dataset]);

  // Load quizzes for this dataset
  useEffect(() => {
    if (!dataset) return;

    const loadQuizzes = async () => {
      try {
        // Query quizzes where datasetSource.path matches this dataset's path
        const allQuizzes = await db.quiz_configs.toArray();
        const matchingQuizzes = allQuizzes.filter((quiz) => {
          if (quiz.datasetSource?.type === "dataset-url") {
            return quiz.datasetSource.path === dataset.path;
          }
          return false;
        });

        // Sort by lastUsedAt (most recent first)
        matchingQuizzes.sort((a, b) =>
          new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
        );

        setQuizzes(matchingQuizzes);
      } catch (err) {
        console.error("Failed to load quizzes:", err);
      }
    };

    loadQuizzes();
  }, [dataset]);

  // Extract sample features for preview
  const sampleFeatures = useMemo(() => {
    if (!geojson) return [];
    return geojson.features.slice(0, 10);
  }, [geojson]);

  // Extract unique values for each property (limited sample)
  const propertyStats = useMemo(() => {
    if (!geojson || !dataset) return {};

    const stats: Record<string, { uniqueCount: number; sampleValues: string[] }> = {};

    dataset.propertyKeys.forEach((key) => {
      const uniqueValues = new Set<string>();
      geojson.features.forEach((feature) => {
        const value = feature.properties?.[key];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((v) => uniqueValues.add(String(v)));
          } else {
            uniqueValues.add(String(value));
          }
        }
      });

      const valuesArray = Array.from(uniqueValues).sort();
      stats[key] = {
        uniqueCount: valuesArray.length,
        sampleValues: valuesArray.slice(0, 5),
      };
    });

    return stats;
  }, [geojson, dataset]);

  // Loading state
  if (loading) {
    return (
      <MainLayout
        title={dataset?.title || "Loading Dataset"}
        subtitle="Loading dataset preview..."
      >
        <div className="container mx-auto p-4 md:p-6 max-w-6xl">
          <div className="text-center py-12 space-y-4">
            <BookMascot size="lg" />
            <p className="text-muted-foreground">Loading GeoJSON data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error || !dataset) {
    return (
      <MainLayout
        title="Dataset Not Found"
        subtitle={error || "The requested dataset could not be found"}
      >
        <div className="container mx-auto p-4 md:p-6 max-w-6xl">
          <Card className="border-dashed border-destructive">
            <CardContent className="p-12 text-center space-y-4">
              <div className="text-6xl">❌</div>
              <h2 className="text-2xl font-semibold text-destructive">
                {error || "Dataset Not Found"}
              </h2>
              <p className="text-muted-foreground">
                {error || "This dataset may have been removed or the ID is invalid."}
              </p>
              <Button onClick={() => navigate("/geojson/browse")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Browse
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title={dataset.title}
      subtitle={dataset.description}
      actions={
        <Button onClick={() => navigate(`/quiz/studio?dataset=${dataset.id}`)}>
          <Sparkles className="w-4 h-4 mr-2" />
          Create Quiz from Dataset
        </Button>
      }
    >
      <div className="container mx-auto p-4 md:p-6 max-w-6xl space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/geojson/browse")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Browse
        </Button>

        {/* Dataset Overview */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                Geography
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{dataset.geography}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold">{dataset.featureCount.toLocaleString()}</p>
                <SizeBadge count={dataset.featureCount} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileJson className="w-4 h-4 text-muted-foreground" />
                Properties
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{dataset.propertyKeys.length}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Info className="w-4 h-4 text-muted-foreground" />
                Topics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{dataset.topics.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Interactive Map */}
        {geojson && (
          <>
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MapIconLucide className="w-5 h-5 text-primary" />
                      Explore the Dataset
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Interactive map with labels - hover to explore features
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="label-property" className="text-sm whitespace-nowrap">
                      Show labels:
                    </Label>
                    <Select
                      value={selectedLabelProperty || ""}
                      onValueChange={(value) => setSelectedLabelProperty(value || null)}
                    >
                      <SelectTrigger id="label-property" className="w-[180px]">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {dataset.propertyKeys.map((key) => (
                          <SelectItem key={key} value={key}>
                            {key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg overflow-hidden border border-border">
                  <ExplorationMap
                    data={geojson}
                    labelProperty={selectedLabelProperty}
                    mapHeight={520}
                    mapStyle="voyager"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  💡 Tip: Hover over regions to see their properties. Labels are positioned at the
                  optimal point within each region.
                </p>
              </CardContent>
            </Card>

            {/* Data Attribution - Always visible below map */}
            {dataset.credits && dataset.credits.length > 0 && (
              <div className="mt-4 text-xs text-muted-foreground text-center space-y-2">
                {dataset.credits.map((credit, idx) => (
                  <div key={idx} className="flex items-start justify-center gap-1.5">
                    <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>{credit.name}</strong>: {credit.description}
                      {credit.url && (
                        <>
                          {' - '}
                          <a
                            href={credit.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            View source
                          </a>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Your Quizzes */}
        {quizzes.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Your Quizzes Using This Dataset</h2>
              <Badge variant="secondary">{quizzes.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quizzes.map((quiz) => (
                <QuizCard key={quiz.signature} quiz={quiz} showActions={false} />
              ))}
            </div>
          </div>
        )}

        {/* Topics */}
        <Card>
          <CardHeader>
            <CardTitle>Topics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {dataset.topics.map((topic) => (
                <Badge key={topic} variant="secondary">
                  {topic}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Property Explorer */}
        <Card>
          <CardHeader>
            <CardTitle>Property Explorer</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Explore the properties available in this dataset
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dataset.propertyKeys.map((key) => {
                const stats = propertyStats[key];
                if (!stats) return null;

                return (
                  <div key={key} className="p-4 rounded-lg border bg-muted/50">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h4 className="font-semibold">{key}</h4>
                      <Badge variant="outline" className="text-xs">
                        {stats.uniqueCount} unique value{stats.uniqueCount !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {stats.sampleValues.map((value, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {value}
                        </Badge>
                      ))}
                      {stats.uniqueCount > 5 && (
                        <Badge variant="secondary" className="text-xs">
                          +{stats.uniqueCount - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sample Features */}
        <Card>
          <CardHeader>
            <CardTitle>Sample Features</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              First {sampleFeatures.length} features from this dataset
            </p>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] rounded-lg border p-4 bg-muted/50">
              <div className="space-y-3">
                {sampleFeatures.map((feature, idx) => (
                  <div key={idx} className="p-3 rounded border bg-background">
                    <div className="text-xs text-muted-foreground mb-2">
                      Feature {idx + 1}
                    </div>
                    <div className="space-y-1 text-sm">
                      {dataset.propertyKeys.map((key) => {
                        const value = feature.properties?.[key];
                        if (value === null || value === undefined) return null;

                        return (
                          <div key={key} className="flex gap-2">
                            <span className="font-medium text-muted-foreground min-w-24">
                              {key}:
                            </span>
                            <span className="break-all">
                              {Array.isArray(value) ? value.join(", ") : String(value)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardContent className="p-8 text-center space-y-4">
            <h3 className="text-2xl font-semibold">Ready to Create a Quiz?</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Use this dataset to create a custom quiz. You'll be able to select which
              properties to quiz on, apply filters, and configure the difficulty.
            </p>
            <Button
              size="lg"
              onClick={() => navigate(`/quiz/studio?dataset=${dataset.id}`)}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Create Quiz from Dataset
            </Button>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
